// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { reduxForm, Field, FormSection } from 'redux-form';
import Heading from '@tcp/core/src/components/common/atoms/Heading';
import TextBox from '@tcp/core/src/components/common/atoms/TextBox';
import Recaptcha from '@tcp/core/src/components/common/molecules/recaptcha/recaptcha';
import getCvvInfo from '@tcp/core/src/components/features/CnC/Checkout/molecules/CVVInfo';
import {
  GLOBAL_CC_LABELS,
  CheckoutOrganismsLabels,
} from '@tcp/core/src/components/features/CnC/Checkout/container/Checkout.labels';
import createValidateMethod from '../../../../../../../utils/formValidation/createValidateMethod';
import getStandardConfig from '../../../../../../../utils/formValidation/validatorStandardConfig';
import Row from '../../../../../../common/atoms/Row';
import Col from '../../../../../../common/atoms/Col';
import BodyCopy from '../../../../../../common/atoms/BodyCopy';
import Button from '../../../../../../common/atoms/Button';
import CreditCardFields from '../../../../../../common/molecules/CreditCardFields';
import constants from '../../../container/AddEditCreditCard.constants';
import AddressDropdown from '../../../molecule/AddressDropdown';
import AddressFields from '../../../../../../common/molecules/AddressFields';
import Address from '../../../../../../common/molecules/Address';
import styles from '../styles/CreditCardForm.style';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
  lblCancel,
  lblSaveCaps,
  lblUpdate,
} from '../../../../../../../utils/labels/globalActions.labels';
import AddEditCreditCardLabels, { ACCOUNT_LABELS } from '../../../views/AddEditCreditCard.labels';

const creditCardProps = {
  cardNumbProps: {
    colSize: {
      small: 6,
      medium: 8,
      large: 6,
    },
  },
  expMonthProps: {
    colSize: {
      small: 3,
      medium: 2,
      large: 3,
    },
  },
  expYearProps: {
    colSize: {
      small: 3,
      medium: 2,
      large: 3,
    },
  },
  cardNumberInnerProps: {
    colSize: {
      small: 6,
      medium: 4,
      large: 12,
    },
  },
  cvvProps: {
    colSize: {
      small: 6,
      medium: 8,
      large: 6,
    },
  },
};

export class CreditCardForm extends React.PureComponent {
  constructor() {
    super();
    this.accountLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_LABELS, AddEditCreditCardLabels);
    this.paymentGC = this.accountLabels?.paymentGC;
    this.common = this.accountLabels?.common;
    this.ccLabels = getLabelFromCopyOrFromTranslation(GLOBAL_CC_LABELS, CheckoutOrganismsLabels);
  }

  static propTypes = {
    className: PropTypes.string,
    addressLabels: PropTypes.shape({}).isRequired,
    addressList: PropTypes.shape({}).isRequired,
    onFileAddressKey: PropTypes.string,
    isEdit: PropTypes.bool,
    mailingAddress: PropTypes.bool,
    backToPaymentClick: PropTypes.func.isRequired,
    pristine: PropTypes.bool.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    initialValues: PropTypes.shape({}).isRequired,
    showCreditCardFields: PropTypes.bool,
    showUserName: PropTypes.bool,
    subHeading: PropTypes.string,
    invalid: PropTypes.bool,
    mapboxAutocompleteTypesParam: PropTypes.string,
    isRecaptchaEnabled: PropTypes.bool.isRequired,
    globalErrorMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    untouch: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    className: '',
    onFileAddressKey: '',
    isEdit: false,
    mailingAddress: false,
    showCreditCardFields: true,
    showUserName: true,
    subHeading: null,
    invalid: false,
  };

  getAddressOptions = () => {
    const { addressList } = this.props;
    let addressOptions = addressList.map((address) => ({
      value: address.addressId,
      title: `${address.firstName} ${address.lastName} ${
        address.preferred === 'true' ? '(Default)' : ''
      }`,
      content: (
        <Address
          address={address}
          showCountry={false}
          showPhone={false}
          isDefault={address.preferred === 'true'}
        />
      ),
    }));
    addressOptions = addressOptions.push({
      value: '',
      title: renderLabel(this.paymentGC?.lbl_payment_addNewAddCta, 'lbl_payment_addNewAddCta'),
      content: (
        <Button fullWidth buttonVariation="variable-width" fill="BLACK">
          {renderLabel(this.paymentGC?.lbl_payment_addNewAddCta, 'lbl_payment_addNewAddCta')}
        </Button>
      ),
    });

    return addressOptions;
  };

  getSelectedAddress = (addressList, onFileAddressKey) => {
    return addressList.find((add) => add.addressId === onFileAddressKey);
  };

  getCreditFieldLabels = () => {
    return {
      creditCardNumber: renderLabel(
        this.paymentGC?.lbl_payment_cardNumber,
        'lbl_payment_cardNumber'
      ),
      expMonth: renderLabel(this.paymentGC?.lbl_payment_expMonth, 'lbl_payment_expMonth'),
      expYear: renderLabel(this.paymentGC?.lbl_payment_expYear, 'lbl_payment_expYear'),
      cvvCode: renderLabel(this.ccLabels?.lbl_creditField_cvvCode, 'lbl_creditField_cvvCode'),
    };
  };

  handleRecaptchaVerify = (token) => {
    const { change } = this.props;
    change('recaptchaToken', token);
  };

  handleRecaptchaExpired = () => {
    const { change } = this.props;
    change('recaptchaToken', '');
  };

  attachReCaptchaRef = (ref) => {
    this.recaptcha = ref;
  };

  attachReCaptchaHiddenRef = (ref) => {
    this.recaptchaHidden = ref;
  };

  resetReCaptcha = () => {
    if (this.recaptcha) {
      this.recaptcha.reset();
    }
    this.handleRecaptchaExpired();
    const { untouch } = this.props;
    untouch('recaptchaToken');
  };

  renderReCaptcha = () => {
    const { isRecaptchaEnabled } = this.props;
    return (
      isRecaptchaEnabled && (
        <div>
          <Row fullBleed>
            <Col ignoreGutter={{ small: true }} colSize={{ small: 4, medium: 2, large: 2 }}>
              <Recaptcha
                theme="light"
                type="image"
                size="normal"
                tabindex="0"
                className="card__recaptcha"
                verifyCallback={this.handleRecaptchaVerify}
                expiredCallback={this.handleRecaptchaExpired}
                dataLocator="credit-card-addcardrecaptchacheckbox"
                ref={this.attachReCaptchaRef}
              />
            </Col>
          </Row>
          <div>
            <Field
              component={TextBox}
              type="hidden"
              name="recaptchaToken"
              className="card__hidden"
              ref={this.attachReCaptchaHiddenRef}
            />
          </div>
        </div>
      )
    );
  };

  render() {
    const {
      className,
      addressLabels,
      addressList,
      onFileAddressKey,
      isEdit,
      backToPaymentClick,
      pristine,
      invalid,
      handleSubmit,
      dispatch,
      initialValues,
      mailingAddress,
      showUserName,
      showCreditCardFields,
      subHeading,
      mapboxAutocompleteTypesParam,
      mapboxSwitch,
      addressFormValues,
      isAccountBillingInternationalAddress,
      cvvCodeRichText,
      isCvvEnabledForAccount,
    } = this.props;
    const showAddressForm = pristine ? !initialValues.onFileAddressKey : !onFileAddressKey;

    const getSubHeading = (pagesubHeading) => {
      let subheading = renderLabel(
        this.paymentGC?.lbl_payment_billingAddress,
        'lbl_payment_billingAddress'
      );
      if (pagesubHeading) {
        subheading = pagesubHeading;
      }
      return subheading;
    };

    return (
      <form
        name={constants.FORM_NAME}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
          this.resetReCaptcha();
        }}
        className={className}
      >
        {showCreditCardFields && (
          <CreditCardFields
            {...this.props}
            {...creditCardProps}
            creditFieldLabels={this.getCreditFieldLabels()}
            cardNumberWrapper
            showCvv={isCvvEnabledForAccount}
            cvvInfo={getCvvInfo({ cvvCodeRichText })}
          />
        )}
        <Heading
          component="h3"
          variant="listMenu"
          className="addressDropdownHeading"
          dataLocator="payment-bilingaddresslabel"
        >
          {getSubHeading(subHeading)}
        </Heading>
        {addressList && addressList.size > 0 && (
          <>
            <Row fullBleed>
              <Col
                colSize={{
                  large: 6,
                  small: 6,
                  medium: 4,
                }}
                className="creditCardForm__addressBook"
              >
                <BodyCopy
                  fontFamily="secondary"
                  fontSize="fs12"
                  fontWeight="black"
                  color="gray.900"
                  dataLocator="payment-billingaddressLbl"
                >
                  {renderLabel(
                    this.paymentGC?.lbl_payment_ccAdressSelect,
                    'lbl_payment_ccAdressSelect'
                  )}
                </BodyCopy>
              </Col>
            </Row>
            <Row fullBleed className="elem-mb-XL">
              <Col
                colSize={{
                  large: 6,
                  small: 6,
                  medium: 4,
                }}
                className="creditCardForm__addressBook"
              >
                <Field
                  name="onFileAddressKey"
                  id="onFileAddressKey"
                  component={AddressDropdown}
                  dataLocator="payment-billingaddressdd"
                  options={this.getAddressOptions()}
                />
              </Col>
              <Col
                colSize={{
                  large: 6,
                  medium: 4,
                  small: 6,
                }}
                className="addressContainer"
              >
                {onFileAddressKey && (
                  <Address
                    address={this.getSelectedAddress(addressList, onFileAddressKey)}
                    showCountry={false}
                    showPhone={false}
                    className="CreditCardForm__address"
                    dataLocatorPrefix="payment"
                    singleLineAddress
                  />
                )}
              </Col>
            </Row>
          </>
        )}

        {showAddressForm && (
          <div className="elem-mb-XL">
            <FormSection name="address">
              <AddressFields
                labels={addressLabels}
                showDefaultCheckbox={false}
                showPhoneNumber={false}
                formName={constants.FORM_NAME}
                formSection="address"
                dispatch={dispatch}
                showUserName={showUserName}
                mapboxAutocompleteTypesParam={mapboxAutocompleteTypesParam}
                mapboxSwitch={mapboxSwitch}
                addressFormValues={addressFormValues}
                isAccountBillingInternationalAddress={isAccountBillingInternationalAddress}
              />
            </FormSection>
          </div>
        )}

        {this.renderReCaptcha()}

        <Row fullBleed className="CreditCardForm__ctaContainer">
          <Col
            className="CreditCardForm__cancel"
            colSize={{ small: 4, medium: 3, large: 3 }}
            offsetLeft={{ small: 1, medium: 1, large: 6 }}
          >
            <Button
              onClick={backToPaymentClick}
              buttonVariation="fixed-width"
              type="button"
              data-locator="payment-cancelbtn"
            >
              {renderLabel(
                getLabelFromCopyOrFromTranslation(
                  `${GLOBAL_ACTIONS_LABELS}.${lblCancel}`,
                  GlobalActionsLabels
                ),
                'lbl_cancel'
              )}
            </Button>
          </Col>
          <Col
            className="CreditCardForm__submit"
            colSize={{ small: 4, medium: 3, large: 3 }}
            offsetLeft={{ small: 1 }}
          >
            <Button
              fill="BLUE"
              disabled={invalid}
              type="submit"
              buttonVariation="fixed-width"
              data-locator="payment-addcardbtn"
            >
              {!mailingAddress &&
                (isEdit
                  ? renderLabel(
                      getLabelFromCopyOrFromTranslation(
                        `${GLOBAL_ACTIONS_LABELS}.${lblUpdate}`,
                        GlobalActionsLabels
                      ),
                      'lbl_update'
                    )
                  : renderLabel(this.common?.lbl_common_addCTA, 'lbl_common_addCTA'))}

              {mailingAddress &&
                renderLabel(
                  getLabelFromCopyOrFromTranslation(
                    `${GLOBAL_ACTIONS_LABELS}.${lblSaveCaps}`,
                    GlobalActionsLabels
                  ),
                  'lbl_save_caps'
                )}
            </Button>
          </Col>
        </Row>
      </form>
    );
  }
}

const validateMethod = createValidateMethod({
  ...getStandardConfig(['cardNumber', 'cvvCode', 'expMonth', 'expYear', 'recaptchaToken']),
  address: AddressFields.addressValidationConfig,
});

export default reduxForm({
  form: constants.FORM_NAME, // a unique identifier for this form
  ...validateMethod,
  enableReinitialize: true,
})(withStyles(CreditCardForm, styles));
